import { copyText } from '@npc/platform'
import { useMutation } from '@tanstack/react-query'
import { Check, Clipboard, KeyRound, Link2, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'

import { redeemRemoteAIInvitation } from '../../app/remote-ai'

export function inviteTokenFromHash(hash: string): string {
  return new URLSearchParams(hash.replace(/^#/, '')).get('invite_token')?.trim() ?? ''
}

export function RemoteAIJoinScreen() {
  const [inviteToken] = useState(() => inviteTokenFromHash(window.location.hash))
  const localDeviceRoute = window.location.pathname === '/local-ai/join'
  const [copied, setCopied] = useState(false)
  const redeem = useMutation({ mutationFn: () => redeemRemoteAIInvitation(inviteToken) })
  const localDevice = redeem.data?.identity_kind === 'local_device' || localDeviceRoute

  /**
   * 复制 API key。
   *
   * 原来这里是一句 `void navigator.clipboard.writeText(k).then(...)`——
   * **没有 catch**。剪贴板不可用时它 reject，`setCopied(true)` 永远不执行，
   * 按钮一动不动、一个字都不说，而这一屏的 API key **只显示这一次**。
   * 用户最合理的反应是再点一次，然后关掉页面，手上什么都没有。
   *
   * 走接缝之后失败是一个返回值，拿不到就不翻成「已复制」——
   * 不假装成功，而 key 本身就渲染在上面那个 code 块里，手动选中仍然拿得到。
   * 把失败明说给他是下一步，那要一条 `docs/copy/` 的模板（归流 0），
   * 这里不自己写死中文。
   */
  async function copyKey(key: string) {
    if ((await copyText(key)).ok) setCopied(true)
  }

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    }
  }, [])

  return (
    <main className="min-h-screen bg-npc-bg px-[20px] py-[56px] text-npc-ink">
      <section className="mx-auto w-full max-w-[680px]">
        <div className="mb-[28px] flex items-center gap-[12px]">
          <span className="flex size-[42px] items-center justify-center rounded-npc-button bg-npc-green-bg text-npc-green-deep">
            <Link2 size={20} aria-hidden />
          </span>
          <div>
            <h1 className="m-0 text-npc-h1-sm font-semibold text-npc-ink-strong">
              {localDevice ? '本机老板 AI 设备授权' : '远程 AI 项目邀请'}
            </h1>
            <p className="mt-[4px] text-npc-body-sm text-npc-ink-soft">
              {localDevice ? '继承当前老板账号的普通平台权限与全部项目范围' : '一次激活，之后可直接开发并通过 API 快速发布'}
            </p>
          </div>
        </div>

        <div className="rounded-npc-card border border-npc-border bg-npc-panel p-[24px] shadow-npc-card">
          {!inviteToken ? (
            <StateBlock title="邀请链接无效" body="链接缺少一次性邀请凭证，请让管理员重新生成。" />
          ) : redeem.data ? (
            <>
              <div className="mb-[20px] flex items-start gap-[12px]">
                <ShieldCheck className="mt-[2px] shrink-0 text-npc-green-deep" size={22} aria-hidden />
                <div>
                  <h2 className="m-0 text-npc-section font-semibold text-npc-ink-strong">{redeem.data.name} 已接入</h2>
                  <p className="mt-[5px] text-npc-body-sm text-npc-ink-soft">
                    {localDevice ? '本机老板 AI 设备，不是远程 AI' : `${redeem.data.provider} / ${redeem.data.model}`} · API key 只在本页显示一次
                  </p>
                </div>
              </div>
              <div className="rounded-npc-code bg-npc-surface-warm p-[14px]">
                <div className="mb-[7px] text-npc-caption text-npc-ink-softer">
                  {localDevice ? '本机设备 API key' : '项目 API key'}
                </div>
                <code className="break-all text-npc-meta text-npc-ink-strong">{redeem.data.api_key}</code>
              </div>
              <button
                type="button"
                className="mt-[12px] inline-flex h-[40px] cursor-pointer items-center justify-center gap-[8px] rounded-npc-button border-none bg-npc-green px-[14px] font-[inherit] text-npc-section-sm font-semibold text-npc-panel"
                onClick={() => { void copyKey(redeem.data.api_key) }}
              >
                {copied ? <Check size={16} aria-hidden /> : <Clipboard size={16} aria-hidden />}
                {copied ? '已复制，立即保存到 secret store' : '复制 API key'}
              </button>
              <div className="mt-[22px] border-t border-npc-border pt-[18px]">
                <div className="mb-[8px] text-npc-caption text-npc-ink-softer">
                  {localDevice ? '常用 API（不是权限白名单）' : '可调用 API'}
                </div>
                <div className="grid gap-[6px]">
                  {redeem.data.apis.map((endpoint) => (
                    <code key={endpoint} className="text-npc-meta text-npc-ink-soft">{endpoint}</code>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-[12px]">
                <KeyRound className="mt-[2px] shrink-0 text-npc-ink-medium" size={22} aria-hidden />
                <div>
                  <h2 className="m-0 text-npc-section font-semibold text-npc-ink-strong">
                    {localDevice ? '激活本机老板 AI 设备' : '激活项目身份'}
                  </h2>
                  <p className="mt-[6px] text-npc-body-sm leading-[1.7] text-npc-ink-soft">
                    {localDevice
                      ? '激活后本链接立即失效，平台只返回一次本机设备 API。它继承授权人当前老板账号的普通平台权限与全部项目范围；红色高危动作仍需人在网页二次确认。'
                      : '激活后本链接立即失效，平台只返回一次项目 API key。无需登录或先建任务；发布时平台只接收一次 exact candidate，不会下发 SSH、服务器或长期 main 写权限。'}
                  </p>
                </div>
              </div>
              {redeem.error ? <p role="alert" className="mt-[16px] text-npc-body-sm text-npc-red-ink">邀请已使用、过期或被撤销。</p> : null}
              <button
                type="button"
                className="mt-[22px] inline-flex h-[42px] w-full cursor-pointer items-center justify-center gap-[8px] rounded-npc-button border-none bg-npc-green px-[14px] font-[inherit] text-npc-section-sm font-semibold text-npc-panel disabled:cursor-not-allowed disabled:opacity-50"
                disabled={redeem.isPending}
                onClick={() => redeem.mutate()}
              >
                <ShieldCheck size={17} aria-hidden />
                {redeem.isPending ? '正在激活…' : localDevice ? '激活并生成设备 API' : '激活并生成 API key'}
              </button>
              <p className="mb-0 mt-[14px] text-npc-meta leading-[1.65] text-npc-ink-softer">
                自动化 Agent 也可读取原链接 fragment 中的 invite_token，通过兼容兑换接口 POST 到 /api/v1/public/remote-ai/invitations/redeem。
              </p>
            </>
          )}
        </div>
      </section>
    </main>
  )
}

function StateBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-[28px] text-center">
      <h2 className="m-0 text-npc-section font-semibold text-npc-ink-strong">{title}</h2>
      <p className="mb-0 mt-[7px] text-npc-body-sm text-npc-ink-soft">{body}</p>
    </div>
  )
}
